import type { CSSProperties } from 'react'
import styles from './birtvyArrive.module.css'

/**
 * The entrance for anything an extract drops on the board (Deck v0.8 › Storyboard, frame 11).
 *
 * The composition puts the website at the world ORIGIN and arranges everything else around it, so a card's
 * own centre already encodes which way it should come from: normalise it and you have the outward
 * direction, no board geometry to thread through the layers. A card at the bottom-left flies in from the
 * bottom-left; one at the top-right from the top-right; the whole set converges.
 */

/**
 * How far a card travels, as a FRACTION of how far out it already sits, clamped.
 *
 * A fixed distance was the thing making this read as popping rather than converging: a picture bleeding off
 * the left edge and one tucked beside the website both slid the same 260px, so the far ones barely seemed to
 * move while the near ones overshot their own gap. Scaling by distance means everything covers the same
 * PROPORTION of its journey, which is what makes a set of them look like one movement.
 */
const TRAVEL_RATIO = 0.24
const TRAVEL_MIN = 150
const TRAVEL_MAX = 440

/** Roughly the furthest anything sits from the website, used to normalise the ripple. */
const MAX_DIST = 2000

/** The ripple's total spread. Inner cards land first and it travels outward — ordering the stagger by
 *  POSITION rather than by array index is what turns twenty-four separate entrances into one wave. */
const RIPPLE_MS = 280

/** Entry blur. Emil's trick for bridging a transition the eye would otherwise read as two separate objects —
 *  here it stands in for motion blur on the fast part of the travel. Small: blur is expensive, and 3px is
 *  enough to soften an edge without becoming an effect of its own. */
const ENTER_BLUR = 3

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

export function arriveProps(
  item: { x: number; y: number; width: number; height: number },
  // Kept for callers that have a meaningful order; the ripple below does not need it.
  _index = 0,
): { className: string; style: CSSProperties } {
  const cx = item.x + item.width / 2
  const cy = item.y + item.height / 2
  const dist = Math.hypot(cx, cy) || 1
  const travel = clamp(dist * TRAVEL_RATIO, TRAVEL_MIN, TRAVEL_MAX)
  return {
    className: styles.arrive,
    style: {
      ['--bds-arrive-x' as string]: `${Math.round((cx / dist) * travel)}px`,
      ['--bds-arrive-y' as string]: `${Math.round((cy / dist) * travel)}px`,
      ['--bds-arrive-delay' as string]: `${Math.round(clamp(dist / MAX_DIST, 0, 1) * RIPPLE_MS)}ms`,
      ['--bds-arrive-blur' as string]: `${ENTER_BLUR}px`,
    },
  }
}
